import React, { useEffect, useMemo, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import MeetingModal, { ShareScreenFull } from '@/models/meeting';
import UserModel from '@/models/user';
import ViewBox, { sizeTypes, viewBoxType } from '../../ViewBox';
import Whiteboard from '@/components/Whiteboard';
import CodeEditor from '@/components/CodeEditor';
import s from './index.less';
import 'swiper/swiper.less';
import BarrageModel from '@/models/barrage';
import RTCModel from '@/models/rtc';
import { motion, useAnimationControls, useMotionValue, useMotionValueEvent } from 'framer-motion';
import classnames from 'classnames';
import { Icon } from '@/components';
import { useSize } from 'ahooks';
import GridLayout from '../GridLayout';
import { SingleColumnLayout } from '../SingleColumnLayout';
import { focused, note } from '@/models';
import FocusedView from '../FocusedView';
import { ViewBoxEvent, eventBus } from '@/utils/event';
import Note from '@/components/Note';
import FullScreen from '../../FullScreen';
interface ViewPanelProps {
  meeting?: MeetingModal;
  user?: UserModel;
  RTCInstance?: RTCModel;
  barrage?: BarrageModel;
}

const ViewPanel: React.FC<ViewPanelProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
    user: store.user,
    RTCInstance: store.rtc,
    barrage: store.barrage,
  };
})(
  observer((props: ViewPanelProps) => {
    const constraintsRef = useRef(null);
    const [dragRightSide, setDragRightSide] = useState<boolean>(false);
    // const [currentMeetingSpeakId, setCurrentMeetingSpeakId] = useState<string>('');
    const { meeting, user, RTCInstance } = props;
    const [main, setMain] = useState<{ yxUid: string; type: viewBoxType }>({
      yxUid: '',
      type: viewBoxType.video,
    });
    const [viewList, setViewList] = useState<{ yxUid: string; type: viewBoxType }[]>([]);
    const x = useMotionValue(0); // 拖拽bar的移动位置
    const singColumnRef = useRef(null);
    const rightGragSize = useSize(singColumnRef);
    const [mvOffset, setMvOffset] = useState<number>(0);

    const focusedIsuser = useMemo(() => {
      return focused?.focusedYuxid === user?.yxuid;
    }, [focused?.focusedYuxid, user?.yxuid]);

    const leftPosRight = useMemo(() => {
      // 左边区域距离右边的距离
      if ((dragRightSide || focused?.focusedYuxid || viewList.length === 0) && !focusedIsuser) {
        return 0;
      }
      return 168 - mvOffset;
    }, [mvOffset, dragRightSide, focused?.focusedYuxid, viewList, focusedIsuser]);

    const controls = useAnimationControls();
    const rightWidth = useMemo(() => {
      if (dragRightSide) {
        return 0;
      }
      return 160 - mvOffset;
    }, [dragRightSide, mvOffset, meeting?.shareScreenFull]);

    const onDrageEnd = (e: any, info: any) => {
      if (!mvOffset && info.offset.x > 80) {
        setDragRightSide(true); // 向右拖动隐藏右边
        controls.set({ x: 160 });
      } else {
        setDragRightSide(false);
      }
    };

    const handleSiderBarClick = () => {
      setDragRightSide(false);
      controls.set({ x: 0 });
      meeting?.setShareScreenFull(ShareScreenFull.INIT);
    };

    useMotionValueEvent(x, 'change', (latest) => {
      setMvOffset(latest);
    });

    useEffect(() => {
      if (meeting?.shareScreenFull === ShareScreenFull.FULL) {
        setDragRightSide(true); // 向右拖动隐藏右边
        controls.set({ x: 160 });
      } else if (meeting?.shareScreenFull === ShareScreenFull.EXIT_FULL) {
      } else {
        setDragRightSide(false);
        controls.set({ x: 0 });
      }
    }, [meeting?.shareScreenFull]);

    useEffect(() => {
      const hostYxuid = meeting?.hostYxUid;
      let temp: typeof main;
      if (meeting?.codeEditorEnabled) {
        temp = {
          yxUid: 'code-editor',
          type: viewBoxType.screen,
        };
      } else if (meeting?.screenUserInfo?.yxUid) {
        temp = {
          yxUid: String(meeting?.screenUserInfo?.yxUid),
          type: viewBoxType.screen,
        };
      } else if (meeting?.whiteboardUserInfo?.yxUid) {
        temp = {
          yxUid: String(meeting?.whiteboardUserInfo?.yxUid),
          type: viewBoxType.screen,
        };
      } else if (meeting?.speakerYxUid) {
        temp = {
          yxUid: String(meeting?.speakerYxUid),
          type: viewBoxType.video,
        };
      } else if (hostYxuid) {
        temp = {
          yxUid: String(hostYxuid),
          type: viewBoxType.video,
        };
      } else {
        const otherOne =
          meeting!.sortYxuids.find((n) => String(n) !== String(user?.yxuid)) || user?.yxuid;
        temp = {
          yxUid: String(otherOne),
          type: viewBoxType.video,
        };
      }
      let list = meeting!.sortYxuids.map((val) => {
        return {
          yxUid: val,
          type: viewBoxType.video,
        };
      });
      if (meeting?.screenUserInfo?.yxUid) {
        const screenYxUid = String(meeting?.screenUserInfo?.yxUid);
        list = [
          {
            yxUid: screenYxUid,
            type: viewBoxType.screen,
          },
          { yxUid: screenYxUid, type: viewBoxType.video },
          ...list.filter((n) => n.yxUid !== screenYxUid),
        ];
      } else if (meeting?.whiteboardUserInfo?.yxUid) {
        const wbYxUid = String(meeting.whiteboardUserInfo?.yxUid);
        list = [
          {
            yxUid: wbYxUid,
            type: viewBoxType.screen,
          },
          { yxUid: wbYxUid, type: viewBoxType.video },
          ...list.filter((n) => n.yxUid !== wbYxUid),
        ];
      }
      // 本人视频卡始终排第一（若本人不是主画面则保留在侧边列表第一位）
      const myCard = list.find(
        (n) => n.yxUid === String(user?.yxuid) && n.type === viewBoxType.video
      );
      if (myCard) {
        list = [
          myCard,
          ...list.filter(
            (n) => !(n.yxUid === String(user?.yxuid) && n.type === viewBoxType.video)
          ),
        ];
      }
      setViewList(list.filter((val) => val.yxUid !== temp.yxUid || val.type !== temp.type));
      setMain(temp);
    }, [
      meeting,
      meeting?.sortYxuids,
      meeting?.screenUserInfo?.yxUid,
      meeting?.whiteboardUserInfo?.yxUid,
      meeting?.hostUid,
      meeting?.speakerYxUid,
      user?.yxuid,
    ]);
    useEffect(() => {
      if (!meeting?.sortYxuids.includes(meeting?.speakerYxUid)) {
        meeting?.lectureClearSpeakerYxUid();
      }
    }, [meeting?.speakerYxUid, meeting?.sortYxuids]);
    useEffect(() => {
      eventBus.emit(ViewBoxEvent.PlayFast);
    }, [focused?.focusedYuxid]);

    if (!main.yxUid) {
      return null;
    }
    return (
      <>
        <div className={s.container}>
          <motion.div className={s.left} style={{ right: leftPosRight }}>
            {main.yxUid === String(meeting?.whiteboardUserInfo?.yxUid) &&
            main.type === viewBoxType.screen &&
            meeting?.whiteboardAuth ? (
              <Whiteboard
                channelId={RTCInstance!.cid}
                appkey={meeting.whiteboardAuth.appkey}
                nonce={meeting.whiteboardAuth.nonce}
                curTime={meeting.whiteboardAuth.curTime}
                checksum={meeting.whiteboardAuth.checksum}
                whiteboardUid={meeting.whiteboardUid}
                uid={user!.mid}
                yxuid={user!.yxuid}
                uname={user!.name}
                willClose={meeting!.whiteboardWillClose}
                whiteboardChannelId={meeting.whiteboardChannelId}
              />
            ) : meeting?.codeEditorEnabled ? (
              <CodeEditor />
            ) : focused?.focusedYuxid && !meeting?.screenUserInfo?.yxUidStr && !focusedIsuser ? (
              <ViewBox
                showTalking={false}
                key={
                  meeting?.screenUserInfo?.yxUidStr ||
                  (focused.focusedYuxid + meeting?.screenUserInfo?.yxUidStr == focused?.focusedYuxid
                    ? viewBoxType.screen
                    : viewBoxType.video)
                }
                yxuid={meeting?.screenUserInfo?.yxUidStr || focused.focusedYuxid}
                type={
                  meeting?.screenUserInfo?.yxUidStr == focused?.focusedYuxid
                    ? viewBoxType.screen
                    : viewBoxType.video
                }
                sizeType={sizeTypes.full}
                className={s.mainViewBox}
              />
            ) : (
              <div className={s.screenViewBox} id="screenViewBox">
                <FullScreen></FullScreen>
                <ViewBox
                  showEmoJ={meeting?.screenUserInfo?.yxUidStr !== focused?.focusedYuxid}
                  showTalking={false}
                  key={main.yxUid + main.type}
                  yxuid={main.yxUid}
                  type={main.type}
                  sizeType={sizeTypes.full}
                  className={s.mainViewBox}
                />
                {meeting?.info?.channelId &&
                  meeting?.screenUid &&
                  meeting?.screenUserInfo?.yxUidStr !== user?.yxuid &&
                  user &&
                  note?.isPCShareAnnotate && (
                    <div className={s.noteBox}>
                      {
                        <Note
                          uid={Number(user.yxuid)}
                          uname={user.name}
                          {...note.shareAnnotateWbYxAuth}
                          originWidth={note.originWidth}
                          originHeight={note.originHeight}
                        ></Note>
                      }
                    </div>
                  )}
              </div>
            )}
          </motion.div>

          <>
            {(!focused.focusedYuxid || focusedIsuser) && (
              <>
                <motion.div className={s.right} style={{ width: rightWidth }}>
                  <div className={s.singleColumn} ref={singColumnRef}>
                    {/* 单列布局 */}
                    {rightGragSize?.width! < 488 && <SingleColumnLayout viewList={viewList} />}

                    {rightGragSize?.width! >= 488 && (
                      <>
                        {/* 两列布局 */}
                        {rightGragSize?.width! < 656 && (
                          <GridLayout list={viewList} limit={5}></GridLayout>
                        )}
                        {/* 三列布局 */}
                        {rightGragSize?.width! > 656 && (
                          <GridLayout list={viewList} limit={9}></GridLayout>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>

                {viewList.length && (
                  <motion.div ref={constraintsRef} className={s.dragF}>
                    <motion.div
                      dragElastic={false}
                      dragMomentum={false}
                      drag="x"
                      whileTap="active"
                      whileHover="active"
                      onDragEnd={onDrageEnd}
                      className={classnames(s.line, dragRightSide ? s.dragRightSlide : '')}
                      style={{ x }}
                      variants={{
                        active: {
                          cursor: 'col-resize',
                        },
                      }}
                      animate={controls}
                      dragConstraints={constraintsRef}
                    ></motion.div>
                  </motion.div>
                )}

                {dragRightSide && (
                  <div className={s.sliderRight} onClick={handleSiderBarClick}>
                    <Icon name="icon-arrow_line_left_1" mode="symbol" size={16}></Icon>
                  </div>
                )}
              </>
            )}
          </>

          {focused?.focusedYuxid && !focusedIsuser && (
            <div className={classnames(s.focusedViewBox)}>
              <FocusedView></FocusedView>
            </div>
          )}
        </div>
      </>
    );
  })
);
export default ViewPanel;
